import api from './api'

/**
 * Serviço de Usuários
 */
export default {
  /**
   * Listar usuários do tenant atual
   * @param {Object} params - Parâmetros opcionais (ex: { expand: 'jobRole,department' })
   */
  list(params = {}) {
    return api.get('/api/users', { params })
  },

  /**
   * Criar novo usuário
   * @param {Object} userData - Dados do usuário
   */
  create(userData) {
    return api.post('/api/users', userData)
  },

  /**
   * Atualizar usuário
   * @param {string} id - ID do usuário
   * @param {Object} userData - Dados atualizados
   */
  update(id, userData) {
    return api.put(`/api/users/${id}`, userData)
  },

  /**
   * Atualizar apenas o status do usuário (mais performático)
   * @param {string} id - ID do usuário
   * @param {string} status - Novo status (ACTIVE, DISABLED, PENDING)
   */
  updateStatus(id, status) {
    return api.put(`/api/users/${id}/status`, { status })
  },

  /**
   * Importar usuários via CSV
   * @param {string} csvContent - Conteúdo do CSV (formato: email,name,role)
   */
  importCSV(csvContent) {
    return api.post('/api/users/import/csv', csvContent, {
      headers: {
        'Content-Type': 'text/plain',
      },
    })
  },

  /**
   * Buscar perfil completo do usuário logado
   * @returns {Promise} Dados completos do perfil (jobRole, department, manager)
   */
  getProfile() {
    return api.get('/api/user/profile')
  },

  /**
   * Upload de avatar do usuário
   * @param {File} file - Arquivo de imagem
   * @returns {Promise<string>} URL da imagem no Cloudinary (TEXT_PLAIN)
   */
  uploadAvatar(file) {
    const formData = new FormData()
    formData.append('file', file)

    // IMPORTANTE: Deletar o Content-Type padrão (application/json) 
    // para permitir que o navegador defina multipart/form-data com boundary
    return api.post('/api/user/avatar/upload', formData, {
      headers: {
        'Content-Type': undefined, // Remove o header padrão
      },
      responseType: 'text', // Backend retorna TEXT_PLAIN
    })
  },

  /**
   * Buscar metas ativas do PDI do usuário logado
   * @param {Object} params - Parâmetros de filtro (startDate, endDate)
   * @returns {Promise} Lista de metas ativas (PENDING ou IN_PROGRESS)
   */
  getMyGoals(params = {}) {
    return api.get('/api/user/pdi/goals', { params })
  },

  /**
   * Buscar próximos passos do PDI ordenados por prazo
   * @param {Object} params - Parâmetros de filtro (startDate, endDate)
   * @returns {Promise} Lista de próximos passos ordenados por deadline
   */
  getNextSteps(params = {}) {
    return api.get('/api/user/pdi/next-steps', { params })
  },

  /**
   * Buscar campanhas ativas para o usuário logado
   * @param {Object} params - Parâmetros de filtro (startDate, endDate)
   * @returns {Promise} Lista de campanhas do tenant
   */
  getMyCampaigns(params = {}) {
    return api.get('/api/user/campaigns', { params })
  },

  /**
   * Buscar feedbacks recebidos com paginação
   * @param {number} limit - Limite de resultados (default: 10)
   * @param {number} offset - Offset para paginação (default: 0)
   * @param {Object} extraParams - Parâmetros extras de filtro (startDate, endDate)
   * @returns {Promise} Objeto com total, limit, offset e items
   */
  getReceivedFeedbacks(limit = 10, offset = 0, extraParams = {}) {
    return api.get('/api/user/feedbacks/received', {
      params: { limit, offset, ...extraParams },
    })
  },

  /**
   * Buscar gestor/mentor do usuário logado
   * @returns {Promise} Dados do gestor/mentor
   */
  getMyManager() {
    return api.get('/api/users/my-manager')
  },

  /**
   * Buscar equipe do usuário logado (subordinados)
   * @returns {Promise} Lista de subordinados
   */
  getMyTeam() {
    return api.get('/api/users/my-team')
  },

  /**
   * Buscar mentores disponíveis para o usuário logado se tornar mentorado
   * @returns {Promise} Lista de mentores disponíveis
   */
  getAvailableMentors() {
    return api.get('/api/users/available-mentors')
  },
}
